import type { SocieteRewardOverrideMeta } from '~/services/LuxLoyalty';

/** Which fields of a Société reward are currently admin-overridden, per `override_meta.fields`. */
export interface OverriddenFields {
  pointsBase: boolean;
  pointsExtraNight: boolean;
  /** Currency codes (e.g. "AUD") whose co-pay is overridden — parsed from "copay:<code>" entries. */
  copay: Set<string>;
  eligible: boolean;
}

const COPAY_PREFIX = 'copay:';

/**
 * Parses `override_meta.fields` (e.g. `["points_base", "copay:AUD", "eligible"]`) into a
 * field-by-field breakdown so the override form can badge which inputs are admin-overridden vs
 * still frozen formula values. Unknown/unrecognised entries are ignored, so a backend field this
 * client doesn't know about yet is silently skipped rather than crashing the form. `null` meta
 * (no override in effect) yields every flag `false` and an empty `copay` set.
 */
export function parseOverriddenFields(meta: SocieteRewardOverrideMeta | null): OverriddenFields {
  const result: OverriddenFields = {
    pointsBase: false,
    pointsExtraNight: false,
    copay: new Set<string>(),
    eligible: false,
  };

  if (!meta) {
    return result;
  }

  for (const field of meta.fields) {
    if (field === 'points_base') {
      result.pointsBase = true;
    } else if (field === 'points_extra_night') {
      result.pointsExtraNight = true;
    } else if (field === 'eligible') {
      result.eligible = true;
    } else if (field.startsWith(COPAY_PREFIX)) {
      result.copay.add(field.slice(COPAY_PREFIX.length));
    }
    // else: unknown entry, ignored.
  }

  return result;
}
